import { Screen, ScreenIdentifiers, MatchRule } from '../../../shared/models/screen.interface';

/**
 * Screen Master Helper - Utility functions for screen detection and matching
 */
export class ScreenMasterHelper {

  /**
   * Map API response to frontend Screen model
   */
  static mapFromAPI(apiData: any): Screen {
    // Parse matchRules - handle various formats from API
    let matchRules: MatchRule[] = [];
    
    if (apiData.matchRules) {
      try {
        let rulesData = apiData.matchRules;
        
        // If it's an object with string keys (PostgreSQL array serialization issue)
        if (typeof rulesData === 'object' && !Array.isArray(rulesData)) {
          // Extract values if it's a key-value object, otherwise extract keys
          const values = Object.values(rulesData).length > 0 ? Object.values(rulesData) : Object.keys(rulesData);
          matchRules = values
            .map((item: any) => this.safeJsonParse(item))
            .filter((rule: any) => rule !== null);
        }
        // If it's a string, parse it
        else if (typeof rulesData === 'string') {
          rulesData = JSON.parse(rulesData);
          // After parsing, if it's still a string, parse again (double-encoded)
          if (typeof rulesData === 'string') {
            rulesData = JSON.parse(rulesData);
          }
          // If it's an array after parsing
          if (Array.isArray(rulesData)) {
            matchRules = rulesData
              .map((rule: any) => this.safeJsonParse(rule))
              .filter((rule: any) => rule !== null);
          }
        }
        // If it's already an array
        else if (Array.isArray(rulesData)) {
          matchRules = rulesData
            .map((rule: any) => this.safeJsonParse(rule))
            .filter((rule: any) => rule !== null);
        }
      } catch (error) {
        matchRules = [];
      }
    }


    // Parse proceedSteps - handle JSON string from API
    let proceedSteps: any[] = [];
    if (apiData.proceedSteps) {
      try {
        if (typeof apiData.proceedSteps === 'string') {
          proceedSteps = JSON.parse(apiData.proceedSteps);
        } else if (Array.isArray(apiData.proceedSteps)) {
          proceedSteps = apiData.proceedSteps;
        }
      } catch (error) {
        proceedSteps = [];
      }
    }

    return {
      id: apiData.id,
      name: apiData.screenName,
      displayName: apiData.screenName,
      description: apiData.description || '',
      matchRules: matchRules,
      minMatch: apiData.minMatch || 2,
      fallbackSteps: proceedSteps,
      isActive: apiData.isActive !== false,
      createdBy: apiData.createdBy,
      createdOn: apiData.createdOn,
      lastModifiedBy: apiData.lastModifiedBy,
      lastModifiedOn: apiData.lastModifiedOn
    };
  }

  /**
   * Map frontend Screen model to API payload
   */
  static mapToAPI(screen: Screen): any {
    const payload: any = {
      screenName: screen.name || screen.displayName,
      matchRules: screen.matchRules || [],
      minMatch: screen.minMatch || 2,
      proceedSteps: screen.fallbackSteps || []
    };

    // Include id for updates
    if (screen.id) {
      payload.id = screen.id;
    }

    // Include description if provided
    if (screen.description) {
      payload.description = screen.description;
    }

    return payload;
  }

  /**
   * Detect current screen identifiers from the browser
   * Uses Electron IPC to extract Workday-specific identifiers
   */
  static async detectCurrentScreen(): Promise<ScreenIdentifiers> {
    const host = (window as any).host;
    if (!host?.detectCurrentScreen) {
      throw new Error('Screen detection not available - ensure browser is open');
    }

    const result = await host.detectCurrentScreen();
    
    if (!result.success) {
      throw new Error(result.error || 'Screen detection failed');
    }

    return result.data;
  }

  /**
   * Evaluate match rules against captured screen identifiers
   * Returns true if enough rules match (>= minMatch threshold)
   */
  static evaluateMatchRules(
    rules: MatchRule[], 
    identifiers: ScreenIdentifiers, 
    minMatch?: number
  ): boolean {
    if (!rules || rules.length === 0) {
      return false;
    }

    let matchedCount = 0;
    const threshold = minMatch || Math.max(2, Math.ceil(rules.length / 2));

    for (const rule of rules) {
      let matched = false;

      switch (rule.type) {
        case 'title_contains':
          if (rule.text && identifiers.title?.toLowerCase().includes(rule.text.toLowerCase())) {
            matched = true;
          }
          break;

        case 'url_contains':
          if (rule.url && identifiers.url?.toLowerCase().includes(rule.url.toLowerCase())) {
            matched = true;
          }
          break;

        case 'element_exists':
          if (rule.selector && identifiers.uniqueWidgets?.includes(rule.selector)) {
            matched = true;
          }
          break;

        case 'heading_contains':
          if (rule.text && identifiers.panelHeadings?.some(h => 
            h.toLowerCase().includes(rule.text!.toLowerCase())
          )) {
            matched = true;
          }
          break;

        case 'breadcrumb_contains':
          if (rule.text && identifiers.breadcrumbs?.some(b => 
            b.toLowerCase().includes(rule.text!.toLowerCase())
          )) {
            matched = true;
          }
          break;
      }

      if (matched) {
        matchedCount++;
      }
    }

    return matchedCount >= threshold;
  }

  /**
   * Find matching screen by evaluating identifiers against all screens
   */
  static findMatchingScreen(
    screens: Screen[], 
    identifiers: ScreenIdentifiers
  ): Screen | null {
    for (const screen of screens) {
      if (screen.matchRules && screen.matchRules.length > 0) {
        const matches = this.evaluateMatchRules(
          screen.matchRules, 
          identifiers, 
          screen.minMatch
        );
        if (matches) {
          return screen;
        }
      }
    }
    return null;
  }

  /**
   * Generate suggested match rules from captured identifiers
   */
  static generateSuggestedRules(identifiers: ScreenIdentifiers): MatchRule[] {
    const rules: MatchRule[] = [];

    // Add title rule if meaningful
    if (identifiers.title && identifiers.title.length > 3) {
      rules.push({
        type: 'title_contains',
        text: identifiers.title
      });
    }

    // Add heading rules for top 3 panel headings
    identifiers.panelHeadings?.slice(0, 3).forEach(heading => {
      if (heading.length > 3) {
        rules.push({
          type: 'heading_contains',
          text: heading
        });
      }
    });

    // Add breadcrumb rule if available
    if (identifiers.breadcrumbs && identifiers.breadcrumbs.length > 0) {
      const lastBreadcrumb = identifiers.breadcrumbs[identifiers.breadcrumbs.length - 1];
      if (lastBreadcrumb && lastBreadcrumb.length > 3) {
        rules.push({
          type: 'breadcrumb_contains',
          text: lastBreadcrumb
        });
      }
    }

    // Add top 5 unique widget IDs
    identifiers.uniqueWidgets?.slice(0, 5).forEach(widget => {
      rules.push({
        type: 'element_exists',
        selector: widget
      });
    });

    return rules;
  }

  /**
   * Validate if current browser page matches a specific screen
   */
  static async validateScreen(
    screen: Screen, 
    identifiers?: ScreenIdentifiers
  ): Promise<boolean> {
    try {
      // Detect current screen if identifiers not provided
      const currentIdentifiers = identifiers || await this.detectCurrentScreen();
      
      if (!screen || !screen.matchRules || screen.matchRules.length === 0) {
        return false;
      }

      return this.evaluateMatchRules(
        screen.matchRules, 
        currentIdentifiers, 
        screen.minMatch
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Safely parse JSON string, returns null on error
   */
  private static safeJsonParse(item: any): any {
    if (typeof item !== 'string') {
      return item;
    }
    try {
      return JSON.parse(item);
    } catch (e) {
      return null;
    }
  }
}
